import React, { Component, ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import SuspensionDTO from '../../../../common/api/dtos/Suspension';
import TableSort from '../../../../common/helpers/TableSort';
import ToolbarControls from '../../../generics/Header/ToolbarControls';
import { TRequestStatus } from '../../../../common/types/RequestStatus';
import { ChangeSortBy } from '../../../../common/helpers/Sorting';
import SortButton from '../../../utils/SortButton/SortButton';
import { getFilteredItems } from '../../../../common/helpers/Filter';
import DateTCell from '../../../utils/DateTCell/DateTCell';
import GhostButtonControl from '../../../controls/GhostButtonControl/GhostButtonControl';
import ToggleControl from '../../../controls/ToggleControl/ToggleControl';
import { listSuspensions } from '../../../../common/api/endpoints/suspensions';
import { listUsers } from '../../../../common/api/endpoints/users';
import UserDTO from '../../../../common/api/dtos/User';
import BreadcrumbControls from '../../../generics/Header/BreadcrumbControls';
import { IProgress, incrementProgress } from '../../../utils/ProgressBar/ProgressBar';
import TextControl from '../../../controls/TextControl/TextControl';
import { withTransitionEvent } from '../../../TransitionEvent';

interface State {
  suspensions?: SuspensionDTO[],
  status: TRequestStatus,
  showSensitive: boolean,
  showDeleted: boolean,
  tableSort: TableSort,
  filterValue: string,
  progress: IProgress,
}

class SuspensionList extends Component<{}, State> {
  availableFilters: string[] = [
    'startDate',
    'endDate',
    'user.name',
    'contract.name',
    'description',
  ]

  constructor(props: {}) {
    super(props);
    this.state = {
      status: 'loading',
      showSensitive: false,
      showDeleted: localStorage.getItem('showDeletedSuspensions') === 'false' || localStorage.getItem('showDeletedSuspensions') === null ? false : true,
      tableSort: new TableSort('user.name', 'asc'),
      filterValue: '',
      progress: {
        currentStep: 0,
        totalSteps: 2
      },
    }
  }

  sensitiveTimeout: number = 0;

  toggleSensitive = (event: ChangeEvent<HTMLInputElement>) => {
    this.state.showSensitive ? this.setState({showSensitive: false}) : this.setState({showSensitive: true});
  }

  toggleDeleted = (event: ChangeEvent<HTMLInputElement>) => {
    localStorage.setItem(event.target.name, JSON.stringify(event.target.checked));
    this.state.showDeleted ? this.setState({showDeleted: false}) : this.setState({showDeleted: true});
  }

  fetchSuspensions = async () => {
    this.setState({
      status: 'loading'
    });
    
    const suspensions = await listSuspensions();

    this.setState({
        status: 'success',
        suspensions: suspensions,
    });
  }

  handleSortChange = (column: string) => {
    this.setState((prevState: State) => {
      return { 
        tableSort: ChangeSortBy(column, prevState.tableSort.sortBy, prevState.tableSort.sortDirection),
       };
    });
  }

  setFilterValue = (ev: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      filterValue: ev.target.value,
    })
  }

  componentDidMount() {
    this.fetchSuspensions();
  }

  componentDidUpdate(prevState: State) {
    if(this.state.showSensitive !== prevState.showSensitive) {
      if(this.state.showSensitive){
        this.sensitiveTimeout = setTimeout(() => {
          this.setState({showSensitive: false});
        }, parseInt(String(process.env.REACT_APP_SENSITIVE_DATA_TIMEOUT))) as unknown as number;
      } else {
        clearTimeout(this.sensitiveTimeout);
      }
    }
  }
  
  renderTHeadRow() {
    return (
      <tr>
        <th>#</th>
        <th>
          <SortButton 
            column='user.name' 
            text='Signatory'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton 
            column='contract.name' 
            text='Issuer'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton 
            column='description' 
            text='Description'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton 
            column='startDate' 
            text='Start date'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton 
            column='endDate' 
            text='End date'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th className="text-center">
          <SortButton 
            column='countable'
            text='Affects paid days'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th></th>
      </tr>
    );
  }

  renderTBodyRow(suspension: SuspensionDTO) {
    return (
      <tr id={`suspension${suspension.id}`} key={`${suspension.id}`}>
        <td></td>
        <td>
          <span>{suspension.user.name}</span><br />
          <small className={!this.state.showSensitive ? 'blurred-item faint-text' : 'blurred-item deblur faint-text'}>{suspension.user.fullName}</small>
        </td>
        <td>
          <span>{suspension.contract.name}</span><br />
          <small className={!this.state.showSensitive ? 'blurred-item faint-text' : 'blurred-item deblur faint-text'}>{suspension.contract.tenant.legalName}</small>
        </td>
        <td>
          <p className="text-chunk">{suspension.description}</p>
        </td>
        <DateTCell date={suspension.startDate}/>
        <DateTCell date={suspension.endDate}/>
        <td className="text-center">
          {suspension.countable &&
            <span className="fas fa-check"></span>
          }
        </td>
        <td className="text-right">
          <GhostButtonControl to={`/suspension/edit/${suspension.id}`} class="ghost-button" icon="fas fa-angle-right"></GhostButtonControl>
        </td>
      </tr>
    );
  }

  render() {
    const filteredSuspensions: Array<SuspensionDTO> = getFilteredItems(this.state.filterValue, this.availableFilters, this.state.suspensions ? this.state.suspensions : []).sort(this.state.tableSort.sortByColumn).filter((suspension: SuspensionDTO) => suspension.deleted === this.state.showDeleted);
    return (
      <div>
        <ToolbarControls>
          <TextControl
            label="Filter"
            placeholder="Filter"
            type="text"
            name="filterBox"
            id="filterBox"
            onChange={this.setFilterValue}
            srOnly={true}
          />
          <ToggleControl
            id="showSensitive"
            name="showSensitive"
            changeMethod={this.toggleSensitive}
            isChecked={this.state.showSensitive}
            labelText="Sensitive data"
                icon="fas fa-shield-alt"
                srOnly={true}
                title="Sensitive data"
          /> 
          <ToggleControl
            id="showDeletedSuspensions"
            name="showDeletedSuspensions"
            changeMethod={this.toggleDeleted}
            isChecked={this.state.showDeleted}
            labelText="Recycle bin"
              icon="fas fa-trash-undo"
              srOnly={true}
              title="Recyle bin"
          />
          <div className="form-group">
            <Link to={'/suspension/add'} className="primary-button"><span className="static-icon"><span className="fas fa-plus-circle"></span></span> <span className="text">Add suspension</span></Link>
          </div>
        </ToolbarControls>
        <BreadcrumbControls
          pageTitle="Suspensions"
          status={this.state.status}
        />
        <div className="tableview-component flex-row fill">
          <div className="column">
            <div className={`
              card
              ${!this.state.suspensions ? 'loader-border' : ''}
              ${this.state.showDeleted ? "flashing-border" : ""}
            `}>
              <div className="inner-v-scroll">
                <table>
                  <thead>
                    {this.renderTHeadRow()}
                  </thead>
                  {this.state.suspensions &&
                    <React.Fragment>
                      <tbody>
                        {filteredSuspensions.map(suspension => (
                          this.renderTBodyRow(suspension)
                        ))}
                      </tbody>
                      <tfoot>
                        {this.state.suspensions.length === 0 &&
                          <tr>
                            <th colSpan={8}>
                              <div className="card">
                                <p className="text-chunk">There are no suspensions defined. Start by <Link className="link-button" to="/suspension/add">adding</Link> the first one.</p>
                              </div>
                            </th>
                          </tr>
                        }
                        {this.state.filterValue &&
                          <tr>
                            <th colSpan={8}>
                              <div className="card">
                                <p className="text-chunk">Showing {filteredSuspensions.length} out of {this.state.suspensions.filter((suspension: SuspensionDTO) => suspension.deleted === this.state.showDeleted).length} results.</p>
                              </div>
                            </th>
                          </tr>
                        }
                      </tfoot>
                    </React.Fragment>
                  }
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
export default withTransitionEvent(SuspensionList);
